import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../components/Layout/DashboardLayout";
import { UpgradeLimitModal } from "../components/shared/UpgradeLimitModal";
import ClientForm, {
  ClientFormValues,
} from "../components/client/ClientForm";
import { clientsApi } from "../api/clientsApi";
import { getApiErrorPayload } from "../utils/apiError";

const AddClientPage: React.FC = () => {
  const navigate = useNavigate();
  const [clientLimitModalOpen, setClientLimitModalOpen] = useState(false);

  const handleCreate = async (values: ClientFormValues) => {
    const now = new Date().toISOString();
    const payload = {
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      email: values.email.trim(),
      phone: values.phone.trim(),
      address: values.address.trim(),
      city: values.city.trim(),
      state: values.state.trim().toUpperCase(),
      zipCode: values.zipCode.trim(),
      ssnLast4: values.ssnLast4.replace(/\D/g, "").slice(0, 4),
      dateOfBirth: values.dateOfBirth,
      equifaxScore: values.equifaxScore,
      experianScore: values.experianScore,
      transUnionScore: values.transUnionScore,
      status: "Active",
      subscriptionStatus: "Active",
      stripeCustomerId: "",
      croaDisclosureSigned: values.croaAccepted,
      croaSignedAt: values.croaAccepted ? now : null,
      serviceAgreementSigned: false,
      serviceAgreementSignedAt: null,
      createdAt: now,
      updatedAt: now,
    };

    try {
      await clientsApi.create(payload);
      navigate("/clients", { replace: true });
    } catch (err: unknown) {
      const apiPayload = getApiErrorPayload(err);
      if (apiPayload?.code === "CLIENT_LIMIT_REACHED") {
        setClientLimitModalOpen(true);
        return;
      }
      throw new Error(
        apiPayload?.message ??
          "Could not create client. Check your connection and try again."
      );
    }
  };

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-3xl space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-white">Add client</h1>
          <p className="text-sm text-slate-400">
            Create a new client profile with bureau scores.
          </p>
        </div>

        <ClientForm
          mode="create"
          submitLabel="Create client"
          onSubmit={handleCreate}
          onCancel={() => navigate("/clients")}
        />
      </div>

      <UpgradeLimitModal
        open={clientLimitModalOpen}
        onClose={() => setClientLimitModalOpen(false)}
        title="You've reached your client limit"
        description="Your current plan has a maximum number of clients. Upgrade to Growth for up to 200 clients, or choose Agency for unlimited clients. You can change your plan anytime from Billing."
      />
    </DashboardLayout>
  );
};

export default AddClientPage;
